import { essentialUsefulInfoText } from "../trips/usefulInfoPresentation";
/**
 * Read-only country facts approved by Mundely administrators.
 * Records awaiting review in usefulInfoInitialImport are deliberately inaccessible.
 * The existing offline/local dataset is the fallback when no approved record exists.
 */
import { doc, getDoc } from "firebase/firestore";
import type { SupportedLanguage } from "../types/domain";
import type { UsefulInfoFieldId } from "../trips/tripStore";
import { db, firebaseReady } from "../firebase/client";

const APPROVABLE_COUNTRY_FIELDS = new Set<UsefulInfoFieldId>([
  "plugType", "currency", "drinkingWater", "taxiApps", "foodDeliveryApps",
  "emergencyNumber", "specialPrecautions", "governmentType", "religion",
  "publicAffection", "lgbtq", "civilRights", "drivingSide"
]);

type ApprovedFields = Partial<Record<UsefulInfoFieldId, string>>;

export interface ApprovedUsefulInfoCountry {
  version: number;
  values: ApprovedFields;
  descriptions: ApprovedFields;
}

function approvedValue(raw: unknown, language: SupportedLanguage): string {
  if (typeof raw === "string") return essentialUsefulInfoText(raw).slice(0, 2000);
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return "";
  const translations = raw as Record<string, unknown>;
  for (const lang of [language, "en", "it", "es"] as const) {
    if (typeof translations[lang] === "string" && translations[lang].trim()) {
      return essentialUsefulInfoText(translations[lang]).slice(0, 2000);
    }
  }
  return "";
}

export async function loadApprovedUsefulInfoCountry(
  countryCode: string, language: SupportedLanguage
): Promise<ApprovedUsefulInfoCountry | null> {
  if (!firebaseReady || !/^[A-Z]{2}$/.test(countryCode)) return null;
  try {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const snap = await Promise.race([
      getDoc(doc(db, "usefulInfoCountries", countryCode)),
      new Promise<null>((resolve) => { timeout = setTimeout(() => resolve(null), 2500); })
    ]).finally(() => { if (timeout) clearTimeout(timeout); });
    if (!snap) return null;
    if (!snap.exists()) return null;
    const data = snap.data();
    if (data.countryCode !== countryCode || data.schemaVersion !== 1) return null;
    const version = Number(data.version);
    if (!Number.isSafeInteger(version) || version < 1) return null;
    const fields = data.values;
    if (!fields || typeof fields !== "object" || Array.isArray(fields)) return null;
    const values: ApprovedFields = {};
    for (const [key, value] of Object.entries(fields)) {
      const field = key as UsefulInfoFieldId;
      if (!APPROVABLE_COUNTRY_FIELDS.has(field)) continue;
      const normalized = approvedValue(value, language);
      if (normalized) values[field] = normalized;
    }
    const descriptions: ApprovedFields = {};
    const sections = data.sections;
    if (sections && typeof sections === "object" && !Array.isArray(sections)) {
      for (const [key, raw] of Object.entries(sections)) {
        const field = key as UsefulInfoFieldId;
        if (!APPROVABLE_COUNTRY_FIELDS.has(field) || !raw || typeof raw !== "object" || Array.isArray(raw)) continue;
        const record = raw as Record<string, unknown>;
        const title = approvedValue(record.title, language);
        const detail = approvedValue(record.description, language);
        if (title) values[field] = title;
        if (detail) descriptions[field] = detail;
      }
    }
    return { version, values, descriptions };
  } catch {
    // Offline, unavailable, or insufficient permissions: keep existing app behavior.
    return null;
  }
}
